<template>
  <v-card class="d-flex flex-column" style="height: 100vh">
    <v-card-title class="error white--text d-flex align-center">
      <v-icon dark left>mdi-tshirt-crew</v-icon>
      WM Trikot 2026 Verwaltung
      <v-spacer></v-spacer>
      <v-btn icon dark @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-4 flex-grow-1" style="overflow-y: auto">
      <div v-if="loading" class="text-center pa-10">
        <v-progress-circular indeterminate color="error" size="64"></v-progress-circular>
        <p class="mt-4">Lade Vorbestellungen...</p>
      </div>
      <div v-else>
        <v-row class="mb-4" align="center">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              label="Suchen nach Name oder E-Mail..."
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              dense
              hide-details
              clearable></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-chip-group v-model="statusFilter" mandatory active-class="error--text">
              <v-chip filter value="all">Alle</v-chip>
              <v-chip filter value="reserved">Reserviert</v-chip>
              <v-chip filter value="collected">Abgeholt / Bezahlt</v-chip>
              <v-chip filter value="canceled">Storniert</v-chip>
            </v-chip-group>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="filteredReservations"
          :items-per-page="15"
          class="elevation-1 rounded-lg"
          :no-data-text="'Keine Vorbestellungen gefunden.'"
          :no-results-text="'Keine Ergebnisse für diese Suche gefunden.'">
          <template v-slot:item.createdBy="{ item }">
            <div v-if="item.createdBy">
              <div class="font-weight-bold">{{ item.createdBy.vorname }} {{ item.createdBy.nachname }}</div>
              <div class="text-caption grey--text">{{ item.createdBy.email }}</div>
            </div>
            <div v-else class="grey--text">Unbekannt</div>
          </template>

          <template v-slot:item.created="{ item }">
            {{ formatDate(item.created) }}
          </template>

          <template v-slot:item.content.items="{ item }">
            <div v-for="(trikot, index) in item.content.items" :key="index" class="text-caption">
              <strong>{{ trikot.quantity }}x</strong> {{ getVariantName(trikot.type) }} (Grösse: {{ trikot.size }})
            </div>
          </template>

          <template v-slot:item.content.totalPrice="{ item }">
            <div class="font-weight-bold">CHF {{ item.content.totalPrice ? Number(item.content.totalPrice).toFixed(2) : "0.00" }}</div>
          </template>

          <template v-slot:item.content.status="{ item }">
            <v-chip :color="getStatusColor(item.content.status)" small label text-color="white">
              {{ getStatusText(item.content.status) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <div v-if="item.content.status === 'reserved'" class="d-flex flex-column gap-2">
              <v-btn small color="success" class="mb-1" @click="updateStatus(item, 'collected')">
                <v-icon left small>mdi-cash-check</v-icon>
                Bezahlt & Abgeholt
              </v-btn>
              <v-btn small color="error" outlined @click="showCancelDialog(item)"> Stornieren </v-btn>
            </div>
            <div v-else-if="item.content.status === 'canceled'">
              <v-btn small color="grey" text disabled>Storniert</v-btn>
            </div>
            <div v-else>
              <v-btn small color="grey" text disabled>Abgeschlossen</v-btn>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card-text>

    <v-dialog v-model="cancelDialog" max-width="400" persistent>
      <v-card v-if="itemToCancel">
        <v-card-title class="text-h5 warning--text">
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Vorbestellung stornieren?
        </v-card-title>
        <v-card-text>
          Möchtest du die Trikot-Bestellung von
          <strong>{{ itemToCancel.createdBy.vorname }} {{ itemToCancel.createdBy.nachname }}</strong>
          wirklich stornieren?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDialog = false">Abbrechen</v-btn>
          <v-btn color="warning" text @click="confirmCancel">Ja, stornieren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"

export default {
  name: "TowelAdmin", // Keeping name
  data() {
    return {
      loading: true,
      reservations: [],
      search: "",
      statusFilter: "all",
      cancelDialog: false,
      itemToCancel: null,
      headers: [
        { text: "Kunde", value: "createdBy", sortable: true },
        { text: "Datum", value: "created", sortable: true },
        { text: "Bestellung", value: "content.items", sortable: false },
        { text: "Preis", value: "content.totalPrice", sortable: false },
        { text: "Status", value: "content.status", sortable: true, align: "center" },
        { text: "Aktionen", value: "actions", sortable: false, align: "right" },
      ],
      typeMap: {
        mann: "Männer",
        frau: "Frauen",
        junge: "Jungen",
        maedchen: "Mädchen",
      },
    }
  },
  computed: {
    filteredReservations() {
      let items = this.reservations
      if (this.statusFilter !== "all") {
        items = items.filter((r) => r.content.status === this.statusFilter)
      }
      if (this.search && this.search.trim() !== "") {
        const searchTerm = this.search.toLowerCase().trim()
        items = items.filter((item) => {
          const createdBy = item.createdBy
          if (!createdBy) return false
          const fullName = `${createdBy.vorname || ""} ${createdBy.nachname || ""}`.toLowerCase()
          return fullName.includes(searchTerm) || (createdBy.email || "").toLowerCase().includes(searchTerm)
        })
      }
      return items
    },
  },
  async created() {
    await this.fetchReservations()
  },
  methods: {
    getVariantName(type) {
      return this.typeMap[type] || type
    },
    async fetchReservations() {
      this.loading = true
      try {
        this.reservations = await api.getTrikotReservations()
      } catch (error) {
        this.$toast.error("Fehler beim Laden der Vorbestellungen.")
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    async updateStatus(item, newStatus) {
      try {
        const updatedItem = await api.updateTrikotReservationStatus(item._id, newStatus)
        const index = this.reservations.findIndex((r) => r._id === item._id)
        if (index !== -1) {
          const newReservations = [...this.reservations]
          newReservations[index] = updatedItem
          this.reservations = newReservations
        }
        this.$toast.success("Status erfolgreich aktualisiert.")
      } catch (error) {
        this.$toast.error("Status konnte nicht aktualisiert werden.")
      }
      this.cancelDialog = false
      this.itemToCancel = null
    },
    showCancelDialog(item) {
      this.itemToCancel = item
      this.cancelDialog = true
    },
    confirmCancel() {
      if (this.itemToCancel) {
        this.updateStatus(this.itemToCancel, "canceled")
      }
    },
    formatDate(timestamp) {
      return this.$moment.unix(timestamp).format("DD.MM.YY, HH:mm")
    },
    getStatusColor(status) {
      switch (status) {
        case "reserved":
          return "warning"
        case "collected":
          return "success"
        case "canceled":
          return "grey"
        default:
          return "grey"
      }
    },
    getStatusText(status) {
      switch (status) {
        case "reserved":
          return "Reserviert"
        case "collected":
          return "Bezahlt/Abgeholt"
        case "canceled":
          return "Storniert"
        default:
          return "Unbekannt"
      }
    },
  },
}
</script>
<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
